/**
 * Press Box → Roster. 2026-10-18, import added 2026-10-19.
 *
 * A standalone Press Box team has no player accounts: the roster is jersey
 * numbers and names (encrypted at rest; the public page shows numbers or
 * initials per the privacy setting). The numbers are what the stats engine
 * matches the narrator's words against, so this list is worth keeping right.
 *
 * Ways in: type them; snap a PHOTO of the printed team list (the vision
 * model on the GPU reads it — no separate OCR step); or upload a
 * spreadsheet (CSV/Excel) or PDF, read by the same model as text. Whatever
 * comes back lands in the editable list below for checking — nothing is
 * saved until Save.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Users, Plus, Trash2, Save, Camera, Upload, Loader2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import PressBoxShell from './PressBoxShell.jsx';

const POSITIONS = [
  ['forward', 'Forward'],
  ['defense', 'Defense'],
  ['goalie', 'Goalie'],
];

const fileText = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsText(file);
});
const fileB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsDataURL(file);
});
async function excelText(file) {
  if (/\.xls$/i.test(file.name || '')) throw new Error('Old Excel (.xls) files are not supported — save as .xlsx or CSV and upload that');
  const { readSheet } = await import('read-excel-file/universal');
  const rows = await readSheet(await file.arrayBuffer());
  return (rows || [])
    .map(r => r.map(c => (c == null ? '' : String(c).trim())).join('\t'))
    .filter(line => line.replace(/\t/g, '').length)
    .join('\n');
}

/** 2026-10-19 — photo / file → the model → editable players. */
function RosterImportCard({ teamId, onPlayers }) {
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const fileRef = useRef(null);

  const run = async (body, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/roster/import`, body, { timeout: 300000 });
      const players = data.players || [];
      toast.success(`Read ${players.length} player${players.length === 1 ? '' : 's'} from the ${label} — check the names, then Save`);
      if (data.notes?.length) toast(data.notes[0], { icon: 'ℹ️', duration: 6000 });
      onPlayers(players);
    } catch (e) {
      toast.error(e.response?.data?.error || `Could not read the ${label}`, { duration: 9000 });
    } finally {
      setBusy(false);
      if (photoRef.current) photoRef.current.value = '';
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const fromPhoto = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return toast.error('Use a PNG, JPEG or WebP photo');
    if (file.size > 8 * 1024 * 1024) return toast.error('The photo must be 8 MB or smaller');
    await run({ kind: 'image', data: await fileB64(file), mimeType: file.type, filename: file.name }, 'photo');
  };

  const fromFile = async (file) => {
    if (!file) return;
    const lower = (file.name || '').toLowerCase();
    try {
      if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return await run({ kind: 'document', text: await excelText(file), filename: file.name }, 'spreadsheet');
      if (lower.endsWith('.csv') || lower.endsWith('.txt')) return await run({ kind: 'document', text: await fileText(file), filename: file.name }, 'file');
      if (lower.endsWith('.pdf')) {
        if (file.size > 14 * 1024 * 1024) return toast.error('The PDF must be 14 MB or smaller');
        return await run({ kind: 'document', data: await fileB64(file), mimeType: 'application/pdf', filename: file.name }, 'PDF');
      }
      toast.error('Use a .csv, .xlsx, .pdf or a photo');
    } catch (e) {
      toast.error(e.message || 'Could not read the file');
    }
  };

  return (
    <div className="card mb-4" data-roster-import>
      <div className="flex items-center gap-2 font-bold mb-2"><Sparkles size={16} style={{ color: 'var(--gold)' }} /> Import the roster</div>
      <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
        Snap a photo of the printed team list, or upload it as a spreadsheet or PDF — the stats AI on the GPU
        reads out the numbers and names. They land in the list below for you to check before saving.
      </div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <button type="button" className="btn-ice" onClick={() => photoRef.current?.click()} disabled={busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Photo of the roster
        </button>
        <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
          <Upload size={14} /> Spreadsheet or PDF
        </button>
        <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp" capture="environment" style={{ display: 'none' }}
          onChange={(e) => fromPhoto(e.target.files?.[0])} />
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.txt" style={{ display: 'none' }}
          onChange={(e) => fromFile(e.target.files?.[0])} />
      </div>
    </div>
  );
}

export default function PressBoxRosterPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let c = false;
    api.get(`/teams/${teamId}/press-box/roster`)
      .then(({ data }) => { if (!c) setRows(data.players || []); })
      .catch((e) => { if (!c) setError(e.response?.data?.error || 'Could not load the roster'); });
    return () => { c = true; };
  }, [teamId]);

  const edit = (i, patch) => {
    setRows((r) => r.map((p, j) => (j === i ? { ...p, ...patch } : p)));
    setDirty(true);
  };
  const add = () => { setRows((r) => [...r, { number: '', name: '', position: 'forward' }]); setDirty(true); };
  const remove = (i) => { setRows((r) => r.filter((_, j) => j !== i)); setDirty(true); };

  // Imported players merge by jersey number: a known number updates the name,
  // a new one is added. Nothing is deleted by an import.
  const mergeImported = (players) => {
    setRows((r) => {
      const byNum = new Map((r || []).map((p, i) => [String(p.number), i]));
      const next = [...(r || [])];
      for (const p of players) {
        const key = p.number != null ? String(p.number) : null;
        const row = { number: p.number ?? '', name: p.name || '', position: p.position || 'forward', imported: true };
        if (key != null && byNum.has(key)) next[byNum.get(key)] = { ...next[byNum.get(key)], ...row };
        else next.push(row);
      }
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    const players = [];
    const seen = new Set();
    for (const p of rows) {
      const number = Number(p.number);
      if (!Number.isInteger(number) || number < 0 || number > 999) return toast.error(`"${p.number || '?'}" is not a jersey number (0–999)`);
      if (seen.has(number)) return toast.error(`#${number} is on the list twice`);
      seen.add(number);
      players.push({ number, name: String(p.name || '').trim(), position: p.position || 'forward' });
    }
    setSaving(true);
    try {
      await api.put(`/teams/${teamId}/press-box/roster`, { players });
      setRows(players);
      setDirty(false);
      toast.success('Roster saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save the roster');
    } finally { setSaving(false); }
  };

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      {error && <div className="card text-center p-4">{error}</div>}
      {!rows && !error && <div className="text-secondary text-sm p-4">Loading…</div>}
      {rows && (
        <>
          <RosterImportCard teamId={teamId} onPlayers={mergeImported} />
          <div className="card mb-6">
            <div className="flex items-center justify-between gap-3 mb-2" style={{ flexWrap: 'wrap' }}>
              <div className="flex items-center gap-2 font-bold"><Users size={16} style={{ color: 'var(--ice)' }} /> Roster</div>
              <div className="flex items-center gap-2">
                <button type="button" className="btn-ghost" onClick={add}><Plus size={14} /> Add player</button>
                <button type="button" className="btn-ice" onClick={save} disabled={!dirty || saving}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              Numbers and names only — nobody needs an account. Names are stored encrypted, and the public
              page shows players the way you picked in Settings (numbers by default, never full names).
            </div>
            {!rows.length && <div className="text-sm p-3" style={{ color: 'var(--text-3)' }}>No players yet — snap a photo of the team list above, or add the jersey numbers by hand.</div>}
            {rows.map((p, i) => (
              <div key={i} className="flex items-center gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
                <input className="input-field" style={{ width: 72, padding: '8px 10px' }} inputMode="numeric" placeholder="#"
                  value={p.number} onChange={(e) => edit(i, { number: e.target.value.replace(/\D/g, '').slice(0, 3) })} aria-label="Jersey number" />
                <input className="input-field" style={{ flex: 1, minWidth: 160, padding: '8px 10px',
                  ...(p.imported ? { borderColor: 'var(--gold)' } : {}) }} placeholder="Name (kept private)"
                  value={p.name || ''} maxLength={120} onChange={(e) => edit(i, { name: e.target.value })} aria-label="Player name" />
                <select className="input-field" style={{ width: 'auto', padding: '8px 10px' }} value={p.position || 'forward'}
                  onChange={(e) => edit(i, { position: e.target.value })} aria-label="Position">
                  {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button type="button" className="btn-ghost" style={{ padding: '8px 10px', color: 'var(--penalty)' }} onClick={() => remove(i)} aria-label="Remove player">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {dirty && <div className="text-xs mt-1" style={{ color: 'var(--puck)' }}>Unsaved changes — hit Save when the list looks right.</div>}
          </div>
        </>
      )}
    </PressBoxShell>
  );
}
